// src/components/SEOClassifier.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const SEOClassifier = () => {
  const [inputText, setInputText] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('single');
  const [batchInput, setBatchInput] = useState('');
  const [batchResults, setBatchResults] = useState(null);

  const classifyText = async () => {
    if (!inputText.trim()) {
      setError('Please enter some text to analyze');
      return;
    }
  
    setLoading(true);
    setError(null);
  
    try {
      const response = await fetch('http://localhost:5000/classify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ text: inputText }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze text');
      }
  
      const data = await response.json();
      
      // Ensure proper types in case backend didn't convert
      const processedData = {
        ...data,
        is_good_seo: Boolean(data.is_good_seo),
        confident: Boolean(data.confident),
        confidence: parseFloat(data.confidence),
        score: parseFloat(data.score)
      };
      
      setResults(processedData);
      
      // Add to history
      setAnalysisHistory(prev => [
        {
          text: inputText,
          result: processedData,
          timestamp: new Date().toISOString(),
        },
        ...prev.slice(0, 9),
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const classifyBatch = async () => {
    if (!batchInput.trim()) {
      setError('Please enter some text to analyze');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const texts = batchInput.split('\n').filter(t => t.trim());
      const response = await fetch('http://localhost:5000/batch-classify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ texts }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze texts');
      }

      const data = await response.json();
      setBatchResults(data.results);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSummary = async () => {
    if (!batchInput.trim()) {
      setError('Please enter some text to analyze');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const texts = batchInput.split('\n').filter(t => t.trim());
      const response = await fetch('http://localhost:5000/batch-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ texts }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze texts');
      }

      const data = await response.json();
      setBatchResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setResults(null);
    setBatchResults(null);
    setInputText('');
    setBatchInput('');
  };

  return (
    <div className="min-h-screen w-full relative bg-white pt-24  p-6 overflow-x-hidden">
        <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-24 -left-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br  from-blue-900 via-purple-950 from-20% to-teal-400  '></span>
        <span className='absolute motion-safe:animate-spin-slow  opacity-65 -z-1 top-0 -right-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-dark_cyan-100 bg-gradient-to-br  from-blue-800 via-purple-400 from-20%  to-cyan-300 '></span>
        <span className='absolute motion-safe:animate-bounce-slow opacity-65 -z-1 bottom-0 -ml-36 w-96 h-96 rounded-full blur-2xl  shadow-lg shadow-emerald-400  bg-gradient-to-br  from-teal-800   to-teal-500 '></span>
      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex flex-col items-center"
        >
          <h1 className="bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text text-6xl font-bold py-5 w-fit">SEO Content Analyzer</h1>
          <p className="bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text w-fit text-2xl">
            Analyze your content for SEO quality using advanced AI classification
          </p>
        </motion.div>

        <div className="bg-transparent rounded-lg backdrop-blur-md shadow-xl rounded-xll overflow-hidden">
          <div className="flex border-b  border-fixify-border">
            <button
              className={`px-6 w-1/2 py-3 font-medium ${activeTab === 'single' ? 'bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 border-b-2 border-vanilla-500' : 'text-fixify-darkBg border-b-2 border-fixify-darkBg'}`}
              onClick={() => setActiveTab('single')}
            >
              Single Analysis
            </button>
            <button
              className={`px-6 w-1/2 py-3 font-medium ${activeTab === 'batch' ?  'bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 border-b-2 border-vanilla-500' : 'text-fixify-darkBg border-b-2 border-fixify-darkBg'}`}
              onClick={() => setActiveTab('batch')}
            >
              Batch Analysis
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'single' ? (
              <div className="space-y-6">
                <div>
                  <label htmlFor="seo-text" className="block text-sm font-medium mb-2">
                    Enter your SEO content
                  </label>
                  <textarea
                    id="seo-text"
                    rows="10"
                    className="w-full  bg-gradient-to-br from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 border border-fixify-primary rounded-lg px-4 py-3  focus:ring-2 focus:ring-fixify-lime focus:border-transparent"
                    placeholder="Paste your SEO content here..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                  />
                </div>

                <div className="flex justify-center gap-5 flex-wrap">
                    <button
                        onClick={classifyText}
                        disabled={loading}
                        className="lg:w-48 md:w-full text-center w-full px-6 py-3 bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 rounded-lg font-medium flex justify-center items-center space-x-2 transition-all"
                    >
                        {loading ? (
                            <>
                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Analyzing...
                            </>
                        ) : (
                            'Analyze Content'
                        )}
                    </button>
                    <button
                        onClick={clearResults}
                        className="lg:w-48 md:w-full text-center w-full px-6 py-3 bg-rich_black-400 text-vanilla-500 hover:bg-rich_black-300 rounded-lg font-medium transition-all"
                    >
                        Clear
                    </button>
                </div>

                {error && (
                  <div className="p-4 bg-rufous-500/20 border border-rufous-500 rounded-lg text-rufous-100">
                    {error}
                  </div>
                )}

                {results && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 space-y-4"
                  >
                    <h3 className="text-xl font-semibold">Analysis Results</h3>
                    <div className={`p-4 rounded-lg border ${results.is_good_seo ? 'border-tiffany_blue-500 bg-tiffany_blue-500/10' : 'border-rufous-500 bg-rufous-500/10'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          SEO Quality: {results.is_good_seo ? (
                            <span className="text-tiffany_blue-300">Good</span>
                          ) : (
                            <span className="text-rufous-300">Poor</span>
                          )}
                        </span>
                        <span className="text-sm opacity-80 font-bold">
                          Confidence: {(results.confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="mt-3">
                        <div className="h-2 bg-rich_black-400 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${results.is_good_seo ? 'bg-tiffany_blue-400' : 'bg-rufous-400'}`}
                            style={{ width: `${results.score * 100}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs mt-1">
                          <span>0%</span>
                          <span>50%</span>
                          <span>100%</span>
                        </div>
                      </div>
                      <div className={`mt-3 font-bold  ${results.is_good_seo ? 'bg-gradient-to-r from-fixify-darkBg to-tiffany_blue-200' : 'bg-gradient-to-r from-rose-500 to-rufous-300'} text-transparent bg-clip-text`}>
                  
                        <p>
                          This content is {results.is_good_seo ? 'likely to perform well' : 'likely to be flagged as spam'} in search engines.
                        </p>
                        {!results.confident && (
                          <p className="mt-1 text-vanilla-300">
                            Note: Confidence is below threshold. Consider reviewing manually.
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label htmlFor="batch-text" className="block text-sm font-medium mb-2">
                    Enter multiple SEO contents (one per line)
                  </label>
                  <textarea
                    id="batch-text"
                    rows="10"
                    className="w-full bg-gradient-to-br from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 border border-fixify-primary rounded-lg px-4 py-3  focus:ring-2 focus:ring-fixify-lime focus:border-transparent"
                    placeholder="Paste multiple SEO contents here, one per line..."
                    value={batchInput}
                    onChange={(e) => setBatchInput(e.target.value)}
                  />
                </div>

                <div className="flex justify-center flex-wrap gap-5">
                  <button
                    onClick={classifyBatch}
                    disabled={loading}
                    className="lg:w-48 md:w-full text-center justify-center w-full px-6 py-3 bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 rounded-lg font-medium flex items-center space-x-2 transition-all"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      'Analyze All'
                    )}
                  </button>
                  <button
                    onClick={getSummary}
                    disabled={loading}
                    className="lg:w-48 md:w-full text-center w-full justify-center px-6 py-3 bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 rounded-lg font-medium flex items-center space-x-2 transition-all"
                  >
                    Get Summary
                  </button>
                  <button
                    onClick={clearResults}
                    className="lg:w-48 md:w-full text-center w-full px-6 py-3 text-vanilla-500 bg-rich_black-400 hover:bg-rich_black-300 rounded-lg font-medium transition-all"
                  >
                    Clear
                  </button>
                </div>

                {error && (
                  <div className="p-4 bg-rufous-500/20 border border-rufous-500 rounded-lg text-rufous-100">
                    {error}
                  </div>
                )}

                {batchResults && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 space-y-4"
                  >
                    <h3 className="text-xl font-semibold">Batch Results</h3>
                    
                    {Array.isArray(batchResults) ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                          {batchResults.map((result, index) => (
                            <div key={index} className={`p-4 rounded-lg border ${result.is_good_seo ? 'border-tiffany_blue-500 bg-tiffany_blue-500/10' : 'border-rufous-500 bg-rufous-500/10'}`}>
                              <div className="flex justify-between items-start">
                                <div className="truncate pr-4">
                                  <p className="font-medium truncate">{result.text}</p>
                                  <p className="text-sm mt-1">
                                    {result.is_good_seo ? (
                                      <span className="text-tiffany_blue-300">Good SEO</span>
                                    ) : (
                                      <span className="text-rufous-300">Poor SEO</span>
                                    )}
                                    <span className="text-vanilla-300 ml-2">(Score: {(result.score * 100).toFixed(1)}%)</span>
                                  </p>
                                  
                                </div>
                                <span className="text-xs opacity-70">
                                  #{index + 1}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="grid grid-cols-3 gap-6">
                          <div className="bg-rich_black-400 p-4 rounded-lg">
                            <h4 className="text-sm font-medium text-vanilla-300 mb-1">Total Analyzed</h4>
                            <p className="text-2xl font-bold text-tiffany_blue-300">{batchResults.total}</p>
                          </div>
                          <div className="bg-tiffany_blue-500/10 border border-tiffany_blue-500 p-4 rounded-lg">
                            <h4 className="text-sm font-medium text-vanilla-300 mb-1">Good SEO</h4>
                            <p className="text-2xl font-bold text-tiffany_blue-300">{batchResults.good_seo_count}</p>
                          </div>
                          <div className="bg-rufous-500/10 border border-rufous-500 p-4 rounded-lg">
                            <h4 className="text-sm font-medium text-vanilla-300 mb-1">Poor SEO</h4>
                            <p className="text-2xl font-bold text-rufous-300">{batchResults.bad_seo_count}</p>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-medium mb-3">Detailed Results</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-rich_black-400">
                              <thead>
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-vanilla-300 uppercase tracking-wider">Content</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-vanilla-300 uppercase tracking-wider">SEO Quality</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-vanilla-300 uppercase tracking-wider">Score</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-rich_black-400">
                                {batchResults.results.map((result, index) => (
                                  <tr key={index}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm max-w-xs truncate">{result.text}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                      {result.is_good_seo ? (
                                        <span className="text-tiffany_blue-300">Good</span>
                                      ) : (
                                        <span className="text-rufous-300">Poor</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                      <div className="w-full bg-rich_black-400 rounded-full h-2">
                                        <div 
                                          className={`h-2 rounded-full ${result.is_good_seo ? 'bg-tiffany_blue-400' : 'bg-rufous-400'}`}
                                          style={{ width: `${result.score * 100}%` }}
                                        ></div>
                                      </div>
                                      <span className="text-xs">{(result.score * 100).toFixed(1)}%</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </div>

        {analysisHistory.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 bg-rich_black-500 bg-opacity-70 backdrop-blur-lg rounded-xl shadow-2xl overflow-hidden p-6"
          >
            <h3 className="text-xl font-semibold mb-4">Recent Analyses</h3>
            <div className="space-y-3">
              {analysisHistory.map((item, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg border ${item.result.is_good_seo ? 'border-tiffany_blue-500 bg-tiffany_blue-500/10' : 'border-rufous-500 bg-rufous-500/10'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="truncate pr-4">
                      <p className="truncate">{item.text}</p>
                      <p className="text-xs mt-1 opacity-80">
                        {new Date(item.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <span className={`text-sm ${item.result.is_good_seo ? 'text-tiffany_blue-300' : 'text-rufous-300'}`}>
                      {item.result.is_good_seo ? 'Good' : 'Poor'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SEOClassifier;